import type { Result } from "@pdfking/errors"
import { ResultMap, Success } from "@pdfking/errors"
import type { QuantitySpec } from "@pdfking/quantity"
import { QuantityToString, StringToQuantity } from "@pdfking/quantity"
import { DISTANCE, FILESIZE, PERCENTAGE, TIME } from "@pdfking/quantity/constants"
import type { Expand } from "@pdfking/structs"
import { NumberQ } from "@pdfking/structs"
import { RecordCreate, RecordMerge, Zip } from "@pdfking/tfunc"

import type { AtomForm } from "#public"

import { CheckRange } from "../../internal/CheckRange"
import { EnsureString } from "../../internal/EnsureString"
import { CreateFormField } from "../api/CreateForm"

type NumberConfig<T extends number = number> = {
  min: T | undefined
  max: T | undefined
  initial: (() => unknown) | undefined
  optional: boolean | undefined
  input_unit: string | undefined
  suggestions: Record<string, string | undefined> | undefined
}

type Config<T extends number = number> = Expand<QuantitySpec<T> & NumberConfig<T>>

type Bounds<T extends number> = { min?: T; max?: T }

type OptionalValue<T, Optional extends boolean> = Optional extends true ? T | undefined : T

// Shared parse: a raw number is taken as-is (broadcast to `length`); a string is
// parsed against `spec` (no spec → unit-less). Either way the magnitudes are
// range-checked. `NullQ(spec)` yields plain numbers (exp 0).
const measure = <T extends number>(
  value: unknown,
  spec: QuantitySpec<T> | undefined,
  length: number,
  bounds: Bounds<T>
): Result<T[]> =>
  ResultMap(
    NumberQ(value)
      ? Success(Array.from({ length }, () => value as T))
      : ResultMap(EnsureString(value), string => StringToQuantity(string, spec, length)),
    values => CheckRange(values, bounds, value)
  )

// A single quantity field. `spec` is optional: without it the field is a plain
// number (no units). Doubles as a `BrandConverter` — the inner form arrives in
// `config` and merges over the spec, carrying `initial` through.
const atom =
  <T extends number>(spec?: QuantitySpec<T>, defaults: Partial<NumberConfig<T>> = {}) =>
  <const Optional extends boolean = false>(
    config: Partial<NumberConfig<T>> & { optional?: Optional } = {}
  ): AtomForm<OptionalValue<T, Optional>> => {
    const merged = RecordMerge(defaults, config) as Partial<Config<T>>
    const resolved = spec ? (RecordMerge(spec, merged) as Config<T>) : undefined
    return CreateFormField<T, Optional>({
      validate: value =>
        ResultMap(measure(value, resolved, 1, merged), values => Success(values[0] as T)),
      format: value => QuantityToString(value, resolved),
      initial: merged.initial,
      optional: merged.optional as Optional,
      suggestions: merged.suggestions,
    })
  }

// A measure record field: parses one string into a branded record type.
// Pass the branded type as a type parameter and the field keys at runtime.
// e.g. DistanceRecordField<Margins<Distance>>(MARGINS_KEYS)
const recordField =
  <T extends number>(spec: QuantitySpec<T>, defaults: Partial<NumberConfig<T>> = {}) =>
  <const K extends Readonly<string[]>, const Optional extends boolean = false>(
    config: Partial<NumberConfig<T>> & { keys: K; optional?: Optional }
  ): AtomForm<OptionalValue<{ [P in K[number]]: T }, Optional>> => {
    type Out = { [P in K[number]]: T }
    const resolved = RecordMerge(spec, defaults, config) as Config<T>
    return CreateFormField<Out, Optional>({
      ...config,
      validate: value =>
        ResultMap(measure(value, resolved, config.keys.length, resolved), values =>
          Success(RecordCreate(Zip(config.keys as Readonly<string[]>, values)) as Out)
        ),
      format: value =>
        QuantityToString(
          config.keys.map(key => (value as Record<string, T>)[key] as T),
          resolved
        ),
    })
  }

export const NumberField = atom()
export const PercentageField = atom(PERCENTAGE)
export const DistanceField = atom(DISTANCE)
export const TimeField = atom(TIME)
export const FileSizeField = atom(FILESIZE)

export const DistanceRecordField = recordField(DISTANCE)
export const PercentageRecordField = recordField(PERCENTAGE)
export const TimeRecordField = recordField(TIME)
export const FileSizeRecordField = recordField(FILESIZE)
